// « Bilan initial » (#/bilan-initial): 7 steps, one question per screen, Appetite copycat world (grey / white / blue, system
// font). Intro, Objectifs, Santé (consent first; « Je préfère en parler directement » skips the questions and keeps no answer),
// Mesures (weight shared with Mon suivi, height shared with Mon profil, coach-configured measures), Photos (progress-photo
// consent, 4 local previews, never uploaded), Habitudes, Récap (« Modifier » per section, « Envoyer à mon coach »).
// Human review only: no diagnosis, no clinical rule, no blocking. Session-local demo (bilan-state.ts); nothing is sent.
import { Button } from "@ubm/ui/components/button";
import { Fragment, useRef, useState, type ReactNode } from "react";
import { useDirectionalSwap } from "./motion-swap";
import { Camera, Check, ChevronLeft, ChevronRight, ImageOff, MessageCircle } from "lucide-react";
import { StatusBar, type Route } from "./parts";
import { store, useStore } from "./store";
import { account, useAccount } from "./compte-state";
import {
  BILAN_DATE, COACH_MEASURES, GOALS, HABITS, HEALTH_QUESTIONS, PHOTO_SHOTS, STEPS, bilan, needsTalk, useBilan,
  type Bilan, type HealthAnswer, type ShotId,
} from "./bilan-state";
import { onboarding } from "./onboarding-state";
import { ShellActions } from "./shell";
import { useShellImmersive } from "./shell-state";
// Questionnaire santé (14/10): only under its explicit configuration, the submission becomes the member's own snapshot.
import { health, isConfigured, useHealth } from "./health-state";

interface PageProps { go: (route: Route) => void; onOutOfScope: (label: string) => void }
const ANSWERS: { v: HealthAnswer; l: string }[] = [{ v: "oui", l: "Oui" }, { v: "non", l: "Non" }, { v: "parler", l: "Je préfère en parler directement" }];
const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const kg = (n: number) => `${n.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} kg`;
const longDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

/** Position saved by « Plus tard », offered by « Reprendre » on the intro (module memory, like the rest of the session demo). */
const resumeAt: { current: { step: number; sub: number } | null } = { current: null };

/** Number of screens of a step (health and photos collapse to their gate when skipped). */
const count = (step: number, b: Bilan) => [1, 2, b.healthChoice === "parler" ? 1 : 1 + HEALTH_QUESTIONS.length, 2 + COACH_MEASURES.length,
  b.photoChoice === "passer" ? 1 : 1 + PHOTO_SHOTS.length, HABITS.length, 1][step] ?? 1;

function Choice({ label, on, onClick, multi }: { label: string; on: boolean; onClick: () => void; multi?: boolean }) {
  return <button type="button" className="cc-bilan-choice" role={multi ? "checkbox" : "radio"} aria-checked={on} data-on={on || undefined} onClick={onClick}>
    <span>{label}</span><span className="cc-bilan-tick" aria-hidden="true">{on && <Check size={14} strokeWidth={3} />}</span>
  </button>;
}

function Signal() {
  return <div className="cc-bilan-signal" role="note"><MessageCircle size={16} strokeWidth={2} /><span><b>Ton coach fera le point avec toi</b><small>Il relira ces réponses avec toi avant d'adapter tes séances. Ce n'est pas un avis médical.</small></span></div>;
}

/* ---------------- Récap (also read-only in Mon suivi) ---------------- */

function Section({ title, onEdit, children }: { title: string; onEdit?: () => void; children: ReactNode }) {
  return <section className="cc-card cc-bilan-section">
    <div className="cc-bilan-section-head"><h2>{title}</h2>{onEdit && <Button variant="ghost" size="sm" onClick={onEdit}>Modifier</Button>}</div>
    {children}
  </section>;
}
const none = <p className="cc-bilan-empty">Non renseigné</p>;

export function BilanRecap({ b, onEdit }: { b: Bilan; onEdit?: (step: number) => void }) {
  const a = useAccount();
  const shared = useStore();
  const edit = (s: number) => onEdit ? () => onEdit(s) : undefined;
  const weight = b.weight?.kg;
  return <div className="cc-bilan-recap">
    <Section title="Objectifs" onEdit={edit(1)}>
      {b.goals.length ? <ul className="cc-bilan-tags">{b.goals.map(g => <li key={g}>{g === "Autre" && b.goalOther.trim() ? `Autre : ${b.goalOther.trim()}` : g}</li>)}</ul> : none}
      {b.priority.trim() && <p className="cc-bilan-quote"><small>Ce qui compte le plus</small>{b.priority.trim()}</p>}
    </Section>
    <Section title="Santé" onEdit={edit(2)}>
      {b.healthChoice === "parler" ? <p className="cc-bilan-line">Tu préfères en parler directement avec ton coach.</p>
        : b.healthChoice === "questions" && a.consents.sensibles ? <ul className="cc-suivi-kv">{HEALTH_QUESTIONS.map(q => { const h = b.health[q.id]; return <li key={q.id}><span>{q.q}</span><b>{h ? ANSWERS.find(x => x.v === h.v)!.l + (h.v === "oui" && h.detail.trim() ? ` · ${h.detail.trim()}` : "") : "Non renseigné"}</b></li>; })}</ul>
        : b.healthChoice === "questions" ? <p className="cc-bilan-line">Accord retiré pour les données de santé : réponses non affichées.</p> : none}
      {needsTalk(b) && (b.healthChoice === "parler" || a.consents.sensibles) && <Signal />}
    </Section>
    <Section title="Mesures" onEdit={edit(3)}>
      <ul className="cc-suivi-kv">
        <li><span>Poids</span><b>{weight !== undefined ? kg(weight) : "Non renseigné"}</b></li>
        <li><span>Taille</span><b>{b.height?.skipped ? "Non renseigné" : `${a.heightCm} cm`}</b></li>
        {COACH_MEASURES.map(m => { const v = b.measures[m.id]; return <li key={m.id}><span>{m.label}</span><b>{v?.value !== undefined ? `${v.value} ${m.unit}` : "Non renseigné"}</b></li>; })}
      </ul>
      {weight !== undefined && shared.weights.some(w => w.date === BILAN_DATE) && <p className="cc-bilan-empty">Le poids est repris dans Mon suivi.</p>}
    </Section>
    <Section title="Photos" onEdit={edit(4)}>
      {b.photoChoice === "passer" ? none
        : b.photoChoice === "photos" && a.consents.photos ? <div className="cc-bilan-thumbs">{PHOTO_SHOTS.map(s => { const sh = b.shots[s.id]; return <figure key={s.id}>
          {sh?.url ? <img src={sh.url} alt={`Photo ${s.label}`} /> : <span className="cc-bilan-thumb-empty" aria-hidden="true"><ImageOff size={16} /></span>}
          <figcaption>{s.label}{!sh?.url && " · Non renseigné"}</figcaption></figure>; })}</div>
        : b.photoChoice === "photos" ? <p className="cc-bilan-line">Accord retiré pour les photos de progression : photos non affichées.</p> : none}
    </Section>
    <Section title="Habitudes" onEdit={edit(5)}>
      <ul className="cc-suivi-kv">{HABITS.map(h => <li key={h.id}><span>{h.q}</span><b>{b.habits[h.id]?.trim() || "Non renseigné"}</b></li>)}</ul>
    </Section>
  </div>;
}

/* ---------------- Flow ---------------- */

export function BilanInitial({ go }: PageProps) {
  useShellImmersive(true); // initial bilan: focused view, no dock
  const b = useBilan();
  const a = useAccount();
  const shared = useStore();
  const [editing, setEditing] = useState(false);
  const dir = useRef(1);
  const file = useRef<HTMLInputElement>(null);
  const stepRef = useRef<HTMLDivElement>(null);
  useDirectionalSwap(stepRef, `${b.step}-${b.sub}-${b.status}`, dir.current, { offset: 24, duration: 320, easing: "cubic-bezier(.22, 1, .36, 1)", reducedDuration: 120 });
  const lastWeight = shared.weights[shared.weights.length - 1]?.kg;
  const [num, setNum] = useState<string>("");

  const moveTo = (step: number, sub: number, d: number) => {
    dir.current = d; setNum("");
    bilan.set({ step, sub, status: b.status === "submitted" ? "submitted" : "draft" });
    document.querySelector(".cc-bilan .cc-scroll")?.scrollTo({ top: 0, behavior: reduced() ? "auto" : "smooth" });
  };
  const next = (after?: Partial<Bilan>) => {
    if (after) bilan.set(after);
    const cur = bilan.get();
    if (cur.sub < count(cur.step, cur) - 1) return moveTo(cur.step, cur.sub + 1, 1);
    if (editing) { setEditing(false); return moveTo(6, 0, 1); }
    moveTo(Math.min(6, cur.step + 1), 0, 1);
  };
  // Opened from the onboarding (#/bilan-initial?retour=onboarding): leaving returns there; a direct visit is unchanged.
  const fromOnb = /retour=onboarding/.test(window.location.hash);
  const exit = () => { if (fromOnb) window.location.hash = "#/onboarding"; else go("accueil"); };
  // In that context only, consents refused during the onboarding are not asked again: the step is skipped without collecting.
  const skipRefused = fromOnb && onboarding.get().consentsDecided;
  const back = () => {
    if (b.sub > 0) return moveTo(b.step, b.sub - 1, -1);
    if (editing) { setEditing(false); return moveTo(6, 0, -1); }
    if (b.step === 0) return exit();
    const prev = b.step - 1;
    moveTo(prev, count(prev, b) - 1, -1);
  };
  const editStep = (s: number) => { setEditing(true); moveTo(s, 0, -1); };
  const pickShot = (id: ShotId, f: File) => {
    const old = b.shots[id]?.url; if (old) URL.revokeObjectURL(old);
    bilan.set({ shots: { ...b.shots, [id]: { url: URL.createObjectURL(f) } } });
  };
  const removeShot = (id: ShotId) => { const old = b.shots[id]?.url; if (old) URL.revokeObjectURL(old); bilan.set({ shots: { ...b.shots, [id]: {} } }); };

  const submitted = b.status === "submitted" && b.step === 6 && !editing;
  const healthOn = isConfigured(useHealth());
  const progress = b.step === 0 ? 0 : (b.step - 1 + (b.sub + 1) / count(b.step, b)) / 6;

  let body: ReactNode;
  let primary: { label: string; on: () => void; disabled?: boolean } | null = { label: "Continuer", on: () => next() };
  let secondary: { label: string; on: () => void } | null = null;

  if (b.step === 0) {
    body = <div className="cc-bilan-intro">
      <h1 className="cc-title">Ton bilan initial</h1>
      <p className="cc-bilan-lead">Ton point de départ</p>
      <ul className="cc-bilan-parts">{["Objectifs", "Santé", "Mesures et photos", "Habitudes"].map((p, i) => <li key={p}><span>{i + 1}</span>{p}</li>)}</ul>
      <p className="cc-bilan-help">Tes réponses aident ton coach à préparer ton programme. Dans l'application, seuls toi et ton coach y avez accès.</p>
      <p className="cc-bilan-demo">Prototype : réponses fictives gardées sur cet appareil le temps de la visite, rien n'est envoyé.</p>
    </div>;
    const at = resumeAt.current;
    primary = b.status === "draft" && at ? { label: "Reprendre", on: () => moveTo(at.step, at.sub, 1) }
      : { label: "Commencer", on: () => { bilan.set({ status: "draft" }); next(); } };
  } else if (b.step === 1) {
    body = b.sub === 0 ? <>
      <h2 className="cc-bilan-q">Qu’est-ce que tu veux améliorer ?</h2><p className="cc-bilan-help">Plusieurs choix possibles.</p>
      <div className="cc-bilan-choices">{GOALS.map(g => <Choice key={g} multi label={g} on={b.goals.includes(g)} onClick={() => bilan.set({ goals: b.goals.includes(g) ? b.goals.filter(x => x !== g) : [...b.goals, g] })} />)}</div>
      {b.goals.includes("Autre") && <label className="cc-account-field"><span>Autre objectif</span><input type="text" value={b.goalOther} onChange={e => bilan.set({ goalOther: e.target.value })} placeholder="Précise ton objectif" /></label>}
    </> : <>
      <h2 className="cc-bilan-q">Qu’est-ce qui compte le plus pour toi ?</h2>
      <label className="cc-account-field"><span>Ta réponse</span><textarea rows={4} value={b.priority} onChange={e => bilan.set({ priority: e.target.value })} placeholder="En quelques mots" /></label>
    </>;
    if (b.sub === 0) primary = { label: "Continuer", on: () => next(), disabled: b.goals.length === 0 };
  } else if (b.step === 2) {
    // Consent guard: without the account's health consent, only the gate renders (any substep, resume included); no answer is read.
    if (skipRefused && !a.consents.sensibles) {
      body = <><h2 className="cc-bilan-q">Questions de santé</h2>
        <p className="cc-bilan-help">Tu as choisi de ne pas partager de données de santé : ton coach en parlera avec toi.</p></>;
      primary = { label: "Continuer", on: () => next({ healthChoice: "parler", health: {} }) };
    } else if (b.sub === 0 || !a.consents.sensibles) {
      body = <>
        <h2 className="cc-bilan-q">Questions de santé</h2>
        <p className="cc-bilan-help">Ces questions aident ton coach à adapter tes séances. Elles concernent des données de santé : elles demandent ton accord, que tu peux retirer dans Paramètres.</p>
        {a.consents.sensibles && <p className="cc-bilan-line"><Check size={14} strokeWidth={3} /> Tu as déjà donné ton accord pour les données de santé.</p>}
        <p className="cc-bilan-demo">Démo : réponses fictives, rien n'est collecté ni envoyé.</p>
      </>;
      primary = { label: a.consents.sensibles ? "Continuer" : "J'autorise", on: () => { if (!a.consents.sensibles) account.set({ consents: { ...a.consents, sensibles: true } }); bilan.set({ healthChoice: "questions" }); moveTo(2, 1, 1); } };
      secondary = { label: "Je préfère en parler directement", on: () => next({ healthChoice: "parler", health: {} }) };
    } else {
      const q = HEALTH_QUESTIONS[b.sub - 1]!;
      const h = b.health[q.id];
      body = <>
        <p className="cc-bilan-count">Question {b.sub} sur {HEALTH_QUESTIONS.length}</p>
        <h2 className="cc-bilan-q">{q.q}</h2>
        <div className="cc-bilan-choices" role="radiogroup" aria-label={q.q}>{ANSWERS.map(x => <Choice key={x.v} label={x.l} on={h?.v === x.v} onClick={() => bilan.set({ health: { ...b.health, [q.id]: { v: x.v, detail: x.v === "oui" ? h?.detail ?? "" : "" } } })} />)}</div>
        {h?.v === "oui" && <label className="cc-account-field"><span>Précise si tu le souhaites</span><textarea rows={3} value={h.detail} onChange={e => bilan.set({ health: { ...b.health, [q.id]: { v: "oui", detail: e.target.value } } })} /></label>}
        {h && h.v !== "non" && <Signal />}
      </>;
      primary = { label: "Continuer", on: () => next(), disabled: !h };
    }
  } else if (b.step === 3) {
    const subs = [{ id: "poids", label: "Poids", unit: "kg" }, { id: "taille", label: "Taille", unit: "cm" }, ...COACH_MEASURES];
    const m = subs[b.sub]!;
    const current = m.id === "poids" ? (b.weight?.kg ?? lastWeight) : m.id === "taille" ? a.heightCm : b.measures[m.id]?.value;
    const value = num !== "" ? num : current !== undefined ? String(current).replace(".", ",") : "";
    const parsed = Number(value.replace(",", "."));
    const valid = value !== "" && Number.isFinite(parsed) && parsed > 0;
    body = <>
      <p className="cc-bilan-count">Mesure {b.sub + 1} sur {subs.length}</p>
      <h2 className="cc-bilan-q">{m.label}</h2>
      {m.id === "poids" && <p className="cc-bilan-help">Prérempli avec ta dernière mesure de Mon suivi.</p>}
      {m.id === "taille" && <p className="cc-bilan-help">Partagée avec Mon profil.</p>}
      {m.id !== "poids" && m.id !== "taille" && <p className="cc-bilan-help">Mesure demandée par ton coach.</p>}
      <label className="cc-bilan-number"><input type="text" inputMode="decimal" value={value} aria-label={`${m.label} en ${m.unit}`} onChange={e => setNum(e.target.value.replace(/[^\d,.]/g, ""))} /><span>{m.unit}</span></label>
    </>;
    primary = { label: "Continuer", disabled: !valid, on: () => {
      if (m.id === "poids") { store.addWeight({ date: BILAN_DATE, kg: Math.round(parsed * 10) / 10 }); next({ weight: { kg: Math.round(parsed * 10) / 10 } }); }
      else if (m.id === "taille") { account.set({ heightCm: Math.round(parsed) }); next({ height: {} }); }
      else next({ measures: { ...b.measures, [m.id]: { value: Math.round(parsed * 10) / 10 } } });
    } };
    secondary = { label: "Passer la mesure", on: () => m.id === "poids" ? next({ weight: { skipped: true } }) : m.id === "taille" ? next({ height: { skipped: true } }) : next({ measures: { ...b.measures, [m.id]: { skipped: true } } }) };
  } else if (b.step === 4) {
    // Same guard for progress photos: no preview source is read without the account's consent.
    if (skipRefused && !a.consents.photos) {
      body = <><h2 className="cc-bilan-q">Photos de progression</h2>
        <p className="cc-bilan-help">Tu as choisi de ne pas ajouter de photos de progression : cette étape est passée.</p></>;
      primary = { label: "Continuer", on: () => next({ photoChoice: "passer", shots: {} }) };
    } else if (b.sub === 0 || !a.consents.photos) {
      body = <>
        <h2 className="cc-bilan-q">Photos de progression</h2>
        <p className="cc-bilan-help">Quatre photos (face, profil droit, profil gauche, dos) pour comparer plus tard. Elles demandent ton accord pour les photos de progression, distinct de l'utilisation de tes photos en communication.</p>
        {a.consents.photos && <p className="cc-bilan-line"><Check size={14} strokeWidth={3} /> Tu as déjà donné ton accord pour les photos de progression.</p>}
        <p className="cc-bilan-demo">Démo : les photos restent en aperçu sur cet appareil, rien n'est envoyé.</p>
      </>;
      primary = { label: a.consents.photos ? "Continuer" : "J'autorise", on: () => { if (!a.consents.photos) account.set({ consents: { ...a.consents, photos: true } }); bilan.set({ photoChoice: "photos" }); moveTo(4, 1, 1); } };
      secondary = { label: "Passer les photos", on: () => { PHOTO_SHOTS.forEach(s => { const u = b.shots[s.id]?.url; if (u) URL.revokeObjectURL(u); }); next({ photoChoice: "passer", shots: {} }); } };
    } else {
      const s = PHOTO_SHOTS[b.sub - 1]!;
      const sh = b.shots[s.id];
      body = <>
        <p className="cc-bilan-count">Photo {b.sub} sur {PHOTO_SHOTS.length}</p>
        <h2 className="cc-bilan-q">{s.label}</h2>
        <div className="cc-bilan-shot">{sh?.url ? <img src={sh.url} alt={`Aperçu ${s.label}`} /> : <span className="cc-bilan-shot-empty"><Camera size={22} strokeWidth={1.8} /><small>Aucune photo</small></span>}</div>
        <div className="cc-bilan-shot-actions">
          <button type="button" className="cc-bilan-secondary" onClick={() => file.current?.click()}>{sh?.url ? "Remplacer" : "Choisir une photo"}</button>
          {sh?.url && <button type="button" className="cc-bilan-secondary" onClick={() => removeShot(s.id)}>Retirer</button>}
        </div>
        <input ref={file} type="file" accept="image/*" hidden onChange={e => { const f = e.target.files?.[0]; if (f) pickShot(s.id, f); e.target.value = ""; }} />
      </>;
      primary = { label: "Continuer", on: () => next(), disabled: !sh?.url };
      secondary = { label: "Passer la photo", on: () => { removeShot(s.id); next({ shots: { ...bilan.get().shots, [s.id]: { skipped: true } } }); } };
    }
  } else if (b.step === 5) {
    const h = HABITS[b.sub]!;
    const v = b.habits[h.id] ?? "";
    body = <>
      <p className="cc-bilan-count">Question {b.sub + 1} sur {HABITS.length}</p>
      <h2 className="cc-bilan-q">{h.q}</h2>
      {h.options.length ? <>
        <div className="cc-bilan-choices" role="radiogroup" aria-label={h.q}>{h.options.map(o => <Choice key={o} label={o} on={v === o} onClick={() => bilan.set({ habits: { ...b.habits, [h.id]: o } })} />)}</div>
        {h.id !== "activite" && <p className="cc-bilan-demo">Choix de démonstration.</p>}
      </> : <label className="cc-account-field"><span>Ta réponse</span><textarea rows={4} value={v} onChange={e => bilan.set({ habits: { ...b.habits, [h.id]: e.target.value } })} placeholder="En quelques mots (facultatif)" /></label>}
    </>;
    primary = { label: "Continuer", on: () => next(), disabled: h.options.length > 0 && !v };
  } else if (submitted) {
    body = <>
      <div className="cc-bilan-done" role="status"><span aria-hidden="true"><Check size={22} strokeWidth={3} /></span><h1 className="cc-title">Ton bilan a été envoyé à ton coach</h1>
        <p className="cc-bilan-help">Envoyé le {longDate(b.submittedAt ?? BILAN_DATE)}. Ton coach le relira avec toi.</p>
        <p className="cc-bilan-demo">Démo : aucun envoi n'a eu lieu.</p>
        {healthOn && <a className="cc-health-link" href="#/sante">Voir mon questionnaire santé</a>}</div>
      <BilanRecap b={b} />
    </>;
    primary = fromOnb ? { label: "Continuer la configuration", on: exit } : { label: "Voir mon bilan", on: () => { window.location.hash = "#/suivi?ouvrir=bilan-initial"; } };
    secondary = fromOnb ? { label: "Voir mon bilan", on: () => { window.location.hash = "#/suivi?ouvrir=bilan-initial"; } } : { label: "Retour à l'accueil", on: () => go("accueil") };
  } else {
    body = <>
      <h2 className="cc-bilan-q">Récapitulatif</h2>
      <p className="cc-bilan-help">Vérifie tes réponses avant de les envoyer. Tu peux modifier chaque partie.</p>
      <BilanRecap b={b} onEdit={editStep} />
    </>;
    primary = { label: "Envoyer à mon coach", on: () => { health.submitBilan(b); bilan.set({ status: "submitted", submittedAt: BILAN_DATE }); dir.current = 1; } };
  }

  return <div className="cc-screen cc-bilan">
    <StatusBar />
    <div className="cc-scroll">
      <header className="cc-bilan-head">
        <button type="button" className="cc-icon-btn" aria-label={b.step === 0 || submitted ? (fromOnb ? "Retour à la configuration" : "Retour à l'accueil") : "Étape précédente"} onClick={() => b.step === 0 || submitted ? exit() : back()}><ChevronLeft size={18} strokeWidth={2} /></button>
        <div className="cc-bilan-head-text"><p className="cc-bilan-count">{b.step === 0 ? "Bilan initial" : submitted ? "Bilan envoyé" : `Étape ${b.step} sur 6 · ${STEPS[b.step]}`}</p>
          {b.step > 0 && !submitted && <div className="cc-bilan-progress" role="progressbar" aria-label="Progression du bilan" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}><i style={{ width: `${progress * 100}%` }} /></div>}</div>
        {b.step > 0 && b.step < 6 && !submitted && <button type="button" className="cc-bilan-quit" onClick={() => { resumeAt.current = { step: b.step, sub: b.sub }; bilan.set({ step: 0, sub: 0 }); exit(); }}>Plus tard</button>}
        <ShellActions compact />
      </header>
      <div ref={stepRef} className="cc-bilan-step"><Fragment key={`${b.step}-${b.sub}-${submitted}`}>{body}</Fragment></div>
      <div className="cc-bilan-actions">
        {primary && <Button variant="primary" size="lg" className="cc-cta" disabled={primary.disabled} onClick={primary.on}>{primary.label}{primary.label === "Continuer" && <ChevronRight size={16} strokeWidth={2.4} />}</Button>}
        {secondary && <button type="button" className="cc-bilan-secondary cc-bilan-secondary--wide" onClick={secondary.on}>{secondary.label}</button>}
      </div>
    </div>
  </div>;
}
